import {
    SyntheticUser,
    SyntheticUserState,
} from '../types';

// FNP-861: `nextWakeAt` is THE operational field — "who wakes next" is the question
// you ask when nothing appears to be happening — so the roster is ordered by it by
// default rather than by creation date.
//
// nextWakeAt is NULLABLE, and a null means two different things depending on state:
// a tombstoned or paused user has no future wake at all, whereas an AWAKE user with
// no nextWakeAt is genuinely unscheduled and worth noticing. Sorting nulls naively
// would either bury the fleet's next action or float dead rows to the top, so:
//
//   1. rows that WILL wake, soonest first          <- what the operator came for
//   2. AWAKE rows with no wake scheduled           <- anomalous; must stay visible
//   3. everything else with no wake (paused/tombstoned), newest-born first
export const nextWakeRank = (record: SyntheticUser): number => {
    if (record.nextWakeAt) {
        return 0;
    }
    return record.state === SyntheticUserState.AWAKE ? 1 : 2;
};

export const sortByNextWake = (records: SyntheticUser[]): SyntheticUser[] =>
    [...records].sort((a, b) => {
        const rankDelta = nextWakeRank(a) - nextWakeRank(b);
        if (rankDelta !== 0) {
            return rankDelta;
        }

        if (a.nextWakeAt && b.nextWakeAt) {
            // Soonest wake first — ISO-8601 timestamps compare correctly as strings.
            return a.nextWakeAt.localeCompare(b.nextWakeAt);
        }

        // No wake on either side: newest-born first so recent arrivals lead.
        return (b.bornAt ?? '').localeCompare(a.bornAt ?? '');
    });

// Is this wake time in the past? An AWAKE user whose wake time has passed is
// overdue — the fleet should have acted and did not. Surfaced in the table so a
// stalled scheduler is visible without cross-referencing the clock by hand.
export const isOverdue = (record: SyntheticUser, now: Date = new Date()): boolean => {
    if (!record.nextWakeAt || record.state !== SyntheticUserState.AWAKE) {
        return false;
    }
    const wakeAt = new Date(record.nextWakeAt)
        .getTime();
    return Number.isFinite(wakeAt) && wakeAt < now.getTime();
};
